import { CSSProperties, FC, MutableRefObject, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface StickyProps {
  topOffset?: number;
  className?: string;
  style?: CSSProperties;

  onStickyStateChange?: (isSticky: boolean) => void;
  wrapperRef?: MutableRefObject<HTMLDivElement>;

  children: ReactNode;
  sentinelId: string;
}

const isElementInViewport = (el?: HTMLElement | null) => {
  if (!el) {
    return false;
  }
  const rect = el.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < (window.innerHeight || document.documentElement.clientHeight);
};

export const Sticky: FC<StickyProps> = ({
  children,
  topOffset = 0,
  className,
  style,
  onStickyStateChange,
  sentinelId,
  wrapperRef,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const [isSticky, setIsSticky] = useState(false);
  const [placeholderHeight, setPlaceholderHeight] = useState(0);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (wrapperRef?.current && !isElementInViewport(wrapperRef.current)) {
          return;
        }

        const next = !entry.isIntersecting;
        setIsSticky((prev) => {
          if (prev !== next) {
            onStickyStateChange?.(next);
            return next;
          }
          return prev;
        });
      },
      {
        root: null,
        threshold: 0,
        rootMargin: `-${topOffset}px 0px 0px 0px`,
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinelRef, topOffset, onStickyStateChange]);

  useLayoutEffect(() => {
    if (!isSticky) return;
    const el = containerRef.current;
    if (!el) return;

    const height = el.offsetHeight;
    setPlaceholderHeight((prev) => (prev === height ? prev : height));
  }, [isSticky, containerRef]);

  const stickyStyle: CSSProperties = {
    position: isSticky ? 'fixed' : 'relative',
    top: isSticky ? topOffset : undefined,
    zIndex: isSticky ? 999 : undefined,
    width: isSticky ? containerRef.current?.offsetWidth : undefined,
    ...style,
  };

  return (
    <>
      <div ref={sentinelRef} style={{ height: '1px', opacity: 0 }} id={sentinelId} />
      {isSticky && <div style={{ height: placeholderHeight }} />}
      <div ref={containerRef} className={className} style={stickyStyle}>
        {children}
      </div>
    </>
  );
};
